import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { Tooltip } from '@material-ui/core'
import StatusPill from '../status-pill'
import Chip from '../chip'
import { colors } from '../tokens'

const useStyles = makeStyles({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: '8px',
    background: '#FFFFFF',
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'border-color 120ms',
    '&:hover': {
      borderColor: colors.primaryLighter,
    },
  },
  cover: {
    position: 'relative',
    aspectRatio: '16 / 10',
    borderRadius: '8px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: colors.hover,
  },
  status: {
    position: 'absolute',
    top: 8,
    left: 8,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  // A yellow note next to the status, e.g. "Unpublished changes".
  statusNote: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '999px',
    background: '#FEF3DD',
    color: '#B06E01',
    fontSize: '11px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    cursor: 'default',
  },
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '14px 8px 8px',
  },
  kindRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: colors.subtle,
  },
  kind: {
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '.08em',
  },
  kindIcon: {
    width: 16,
    height: 16,
  },
  title: {
    minHeight: 40,
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: 1.35,
    color: colors.navy,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  footer: {
    marginTop: 'auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    borderRadius: 'inherit',
    background: 'rgba(255,255,255,.78)',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.navy,
    cursor: 'default',
  },
})

/**
 * Grid card for courses and learning paths.
 *
 * @param {string} cover - image URL or CSS background value
 * @param {string} [status] - 'Published' | 'Draft'
 * @param {{ label: string, tooltip?: string }} [statusNote] - yellow note next to the status
 * @param {string[]} [tags] - plain tag labels
 * @param {React.ReactNode} [tagsSlot] - custom tag rendering (replaces `tags`)
 * @param {React.ReactNode} footer - footer content (left + right slots)
 * @param {string} [busyLabel] - shows a blocking overlay, e.g. "Duplicating…"
 */
const LibraryCard = ({
  cover,
  coverKey,
  status,
  statusNote,
  kind,
  kindIcon,
  title,
  tags = [],
  tagsSlot,
  footer,
  busyLabel,
  onClick,
}) => {
  const classes = useStyles()
  // Plain URLs become url(); gradients are passed through as images.
  const backgroundImage = /^(https?:|\/)/.test(cover) ? `url(${cover})` : cover

  return (
    <div
      className={classes.root}
      role="link"
      tabIndex={0}
      onClick={busyLabel ? undefined : onClick}
      onKeyDown={event => {
        if (event.key === 'Enter' && !busyLabel) onClick()
      }}
    >
      {/* coverKey keeps a default cover's art the same for this card. */}
      <div
        className={classes.cover}
        style={{ backgroundImage }}
        data-cover-key={coverKey}
      >
        {status && (
          <div className={classes.status}>
            <StatusPill status={status} />
            {statusNote && (
              <Tooltip title={statusNote.tooltip || ''} placement="top" arrow>
                <span
                  className={classes.statusNote}
                  onClick={event => event.stopPropagation()}
                >
                  {statusNote.label}
                </span>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      <div className={classes.body}>
        <div className={classes.kindRow}>
          <span className={classes.kind}>{kind}</span>
          <Icon icon={kindIcon} className={classes.kindIcon} />
        </div>
        <div className={classes.title} title={title}>
          {title}
        </div>
        {tagsSlot}
        {!tagsSlot && tags.length > 0 && (
          <div className={classes.tags}>
            {tags.map(tag => (
              <Chip key={tag} label={tag} />
            ))}
          </div>
        )}
        <div
          className={classes.footer}
          onClick={event => event.stopPropagation()}
        >
          {footer}
        </div>
      </div>
      {busyLabel && (
        <div className={classes.overlay}>
          <Icon icon="eos-icons:loading" width={20} height={20} />
          {busyLabel}
        </div>
      )}
    </div>
  )
}

export default LibraryCard
